import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from './calendar.module.scss';

const WEEKDAY_MAP = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a, b) =>
	!!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function getFirstDayOfAWeekFromStorage() {
	const s = localStorage.getItem('currentFirstDayOfAWeek');
	return s || 'monday';
}

function getWeekDayLabels(firstDayOfAWeek) {
	const start = WEEKDAY_MAP[(firstDayOfAWeek || 'monday').toLowerCase()] ?? 1;
	return [...WEEKDAY_SHORT.slice(start), ...WEEKDAY_SHORT.slice(0, start)];
}

/**
 * Close an open popover when the user presses outside `containerRef` (mouse or touch).
 * Replaces a full-screen backdrop so the page can still scroll while the calendar is open.
 */
export function useCloseOnOutsidePress(isOpen, containerRefOrRefs, onClose) {
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	const refsHolder = useRef(containerRefOrRefs);
	refsHolder.current = containerRefOrRefs;

	useEffect(() => {
		if (!isOpen) return;
		const handle = (e) => {
			const raw = refsHolder.current;
			const list = Array.isArray(raw) ? raw : [raw];
			for (const r of list) {
				if (r?.current?.contains(e.target)) return;
			}
			onCloseRef.current();
		};
		document.addEventListener('pointerdown', handle);
		return () => document.removeEventListener('pointerdown', handle);
	}, [isOpen]);
}

const POPOVER_WIDTH_PX = 240;
const POPOVER_GAP_PX = 8;
const VIEWPORT_EDGE_PAD = 8;

function horizontalLeftForAnchor(rect, popoverAlign) {
	let left;
	if (popoverAlign === 'end') left = rect.right - POPOVER_WIDTH_PX;
	else if (popoverAlign === 'start') left = rect.left;
	else left = rect.left + rect.width / 2 - POPOVER_WIDTH_PX / 2;
	return Math.max(
		VIEWPORT_EDGE_PAD,
		Math.min(left, window.innerWidth - POPOVER_WIDTH_PX - VIEWPORT_EDGE_PAD)
	);
}

function computeFixedStyleAbove(anchorRef, popoverAlign) {
	const el = anchorRef?.current;
	if (!el || typeof el.getBoundingClientRect !== 'function') return null;
	const rect = el.getBoundingClientRect();
	const left = horizontalLeftForAnchor(rect, popoverAlign);
	const ih = typeof window !== 'undefined' ? window.innerHeight : 0;
	return {
		position: 'fixed',
		left,
		bottom: ih - rect.top + POPOVER_GAP_PX,
		top: 'auto',
		right: 'auto',
		width: POPOVER_WIDTH_PX,
		transform: 'none',
		zIndex: 1001,
		boxSizing: 'border-box',
	};
}

function computeFixedStyleBelow(anchorRef, popoverAlign) {
	const el = anchorRef?.current;
	if (!el || typeof el.getBoundingClientRect !== 'function') return null;
	const rect = el.getBoundingClientRect();
	const left = horizontalLeftForAnchor(rect, popoverAlign);
	const top = rect.bottom + POPOVER_GAP_PX;
	return {
		position: 'fixed',
		left,
		top,
		bottom: 'auto',
		right: 'auto',
		width: POPOVER_WIDTH_PX,
		transform: 'none',
		zIndex: 1001,
		boxSizing: 'border-box',
	};
}

/**
 * @param {'singleDate'|'range'|'both'} props.selectType
 * @param {Date} [props.initialStart]
 * @param {Date} [props.initialEnd]
 * @param {() => void} props.onCancel
 * @param {(start: Date) => void | ((start: Date, end: Date) => void)} props.onApply — single-date mode: called immediately with the clicked day; range mode: called when Apply is pressed with (start, end)
 * @param {'center'|'start'|'end'} [props.popoverAlign] — horizontal alignment under the anchor (default center).
 * @param {'below'|'above'} [props.popoverPlacement] — vertical placement relative to the anchor (default below).
 * @param anchorRef — when set, popover is portaled to document.body with fixed position (escapes overflow clipping).
 * @param popoverRef — optional ref on the popover root (for outside-press when using a portal).
 * @param autoVerticalPlacement — with anchorRef: if the anchor’s vertical midpoint is below the visible viewport midpoint, open above (popover bottom flush above the input top with gap); otherwise open below.
 */
export function CalendarPicker({
	selectType = 'range',
	initialStart,
	initialEnd,
	onCancel,
	onApply,
	popoverAlign = 'center',
	popoverPlacement = 'below',
	anchorRef = null,
	popoverRef = null,
	autoVerticalPlacement = false,
}) {
	const [layoutEpoch, setLayoutEpoch] = useState(0);
	const useViewportFixed = anchorRef != null;

	useEffect(() => {
		if (!useViewportFixed) return;
		const bump = () => setLayoutEpoch((n) => n + 1);
		window.addEventListener('resize', bump);
		document.addEventListener('scroll', bump, true);
		const vv = window.visualViewport;
		vv?.addEventListener('resize', bump);
		vv?.addEventListener('scroll', bump);
		return () => {
			window.removeEventListener('resize', bump);
			document.removeEventListener('scroll', bump, true);
			vv?.removeEventListener('resize', bump);
			vv?.removeEventListener('scroll', bump);
		};
	}, [useViewportFixed]);

	useLayoutEffect(() => {
		if (!useViewportFixed) return;
		setLayoutEpoch((n) => n + 1);
	}, [useViewportFixed]);

	const viewportFixedStyle = useMemo(() => {
		if (!useViewportFixed) return null;
		void layoutEpoch;
		const el = anchorRef?.current;
		if (!el || typeof el.getBoundingClientRect !== 'function') return null;
		const rect = el.getBoundingClientRect();
		let effectivePlacement = popoverPlacement;
		if (autoVerticalPlacement) {
			const vh =
				typeof window !== 'undefined'
					? window.visualViewport?.height ?? window.innerHeight
					: 0;
			const midY = (rect.top + rect.bottom) / 2;
			effectivePlacement = midY > (vh / 1.4) ? 'above' : 'below';
		}
		return effectivePlacement === 'above'
			? computeFixedStyleAbove(anchorRef, popoverAlign)
			: computeFixedStyleBelow(anchorRef, popoverAlign);
	}, [
		useViewportFixed,
		popoverAlign,
		popoverPlacement,
		autoVerticalPlacement,
		layoutEpoch,
		anchorRef,
	]);

	function setPopoverEl(node) {
		if (popoverRef == null) return;
		if (typeof popoverRef === 'function') popoverRef(node);
		else popoverRef.current = node;
	}
	const firstDayOfAWeek = getFirstDayOfAWeekFromStorage();
	const [internalMode, setInternalMode] = useState('range');

	const isRangeSelection =
		selectType === 'range' || (selectType === 'both' && internalMode === 'range');

	const today = stripTime(new Date());
	const anchor = initialStart || today;
	const [month, setMonth] = useState(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
	const [start, setStart] = useState(initialStart ? stripTime(initialStart) : null);
	const [end, setEnd] = useState(initialEnd && isRangeSelection ? stripTime(initialEnd) : null);
	const [hoverDate, setHoverDate] = useState(null);

	const firstDayIdx = WEEKDAY_MAP[(firstDayOfAWeek || 'monday').toLowerCase()] ?? 1;
	const weekDays = getWeekDayLabels(firstDayOfAWeek);

	const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
	const leading = (firstOfMonth.getDay() - firstDayIdx + 7) % 7;
	const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
	const cells = [];
	for (let i = leading; i > 0; i--) {
		cells.push({ date: new Date(month.getFullYear(), month.getMonth(), 1 - i), otherMonth: true });
	}
	for (let i = 1; i <= daysInMonth; i++) {
		cells.push({ date: new Date(month.getFullYear(), month.getMonth(), i), otherMonth: false });
	}
	while (cells.length < 42) {
		const last = cells[cells.length - 1].date;
		cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), otherMonth: true });
	}

	const setModeAndMaybeClear = (mode) => {
		setInternalMode(mode);
		if (mode === 'single') {
			setEnd(null);
		}
	};

	const handleClick = (date) => {
		const d = stripTime(date);
		if (!isRangeSelection) {
			setStart(d);
			setEnd(null);
			onApply(d);
			return;
		}
		if (!start || (start && end)) {
			setStart(d);
			setEnd(null);
		} else if (d < start) {
			setEnd(start);
			setStart(d);
		} else {
			setEnd(d);
		}
	};

	const previewEnd =
		isRangeSelection && start && !end && hoverDate && hoverDate > start ? hoverDate : null;
	const rangeEndForDisplay = end || previewEnd;

	const inRange = (d) => {
		if (!isRangeSelection || !start || !rangeEndForDisplay) return false;
		return d > start && d < rangeEndForDisplay;
	};

	const isRangeStart = (d) => start && sameDay(d, start);

	const isRangeEnd = (d) => {
		if (!isRangeSelection) {
			return start && sameDay(d, start);
		}
		return (end && sameDay(d, end)) || (!end && previewEnd && sameDay(d, previewEnd));
	};

	const monthLabel = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
	const fmt = (d) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

	const handleApply = () => {
		if (!start) return;
		if (!end) return;
		onApply(start, end);
	};

	const anchoredClass =
		(popoverAlign === 'end'
			? `${styles.pickerPopover} ${styles.pickerPopoverAlignEnd}`
			: popoverAlign === 'start'
				? `${styles.pickerPopover} ${styles.pickerPopoverAlignStart}`
				: styles.pickerPopover) +
		(popoverPlacement === 'above' && !viewportFixedStyle ? ` ${styles.pickerPopoverAbove}` : '');

	const panel = (
		<div
			ref={popoverRef != null ? setPopoverEl : undefined}
			className={viewportFixedStyle ? styles.pickerPopoverSurface : anchoredClass}
			style={viewportFixedStyle || undefined}
			onClick={(e) => e.stopPropagation()}
		>
			{selectType === 'both' ? (
				<div className={styles.modeToggle} role="group" aria-label="Selection mode">
					<button
						type="button"
						className={internalMode === 'single' ? styles.modeActive : ''}
						onClick={() => setModeAndMaybeClear('single')}
					>
						Single date
					</button>
					<button
						type="button"
						className={internalMode === 'range' ? styles.modeActive : ''}
						onClick={() => setInternalMode('range')}
					>
						Range
					</button>
				</div>
			) : null}
			<div className={styles.pickerHeader}>
				<button
					type="button"
					onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
					aria-label="Previous month"
				>
					<FaChevronLeft />
				</button>
				<span>{monthLabel}</span>
				<button
					type="button"
					onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
					aria-label="Next month"
				>
					<FaChevronRight />
				</button>
			</div>
			<div className={styles.weekDayRow}>
				{weekDays.map((d) => (
					<span key={d}>{d}</span>
				))}
			</div>
			<div className={styles.daysGrid} onMouseLeave={() => setHoverDate(null)}>
				{cells.map((c, i) => {
					const classes = [styles.dayCell];
					if (c.otherMonth) classes.push(styles.otherMonth);
					if (inRange(c.date)) classes.push(styles.inRange);
					if (isRangeStart(c.date)) classes.push(styles.rangeStart);
					if (isRangeEnd(c.date)) classes.push(styles.rangeEnd);
					if (sameDay(c.date, today)) classes.push(styles.today);
					return (
						<button
							key={i}
							type="button"
							className={classes.join(' ')}
							onMouseEnter={() => setHoverDate(c.date)}
							onClick={() => handleClick(c.date)}
						>
							{c.date.getDate()}
						</button>
					);
				})}
			</div>
			{isRangeSelection ? (
				<div className={styles.pickerFooter}>
					<span className={styles.rangeText}>{`${fmt(start)} \u2013 ${fmt(end)}`}</span>
					<div className={styles.buttons}>
						<button type="button" className={styles.secondary} onClick={onCancel}>
							Cancel
						</button>
						<button
							type="button"
							className={styles.primary}
							disabled={!start || !end}
							onClick={handleApply}
						>
							Apply
						</button>
					</div>
				</div>
			) : null}
		</div>
	);

	if (useViewportFixed) {
		if (!viewportFixedStyle) return null;
		return createPortal(panel, document.body);
	}
	return panel;
}
